import { FC, useMemo } from 'react';
import { useUserAccessFormState } from '../../hooks/useUserAccessFormState';
import UserAccessDialogLayout from '../UserAccessDialogLayout';
import UserAccessForm from '../UserAccessForm';
import UserInfoSection from '../UserInfoSection';
import { USER_ACCESS_MODAL_MODE, USER_ACTION_SUCCESS_TOASTS } from '../../constants';
import { CLIENT_USER_ROLE, ClientUser, UserAccess } from '@api/types/client-user-management';
import { extractSubmoduleIds } from '../../utils';
import { useUpdateUserAccessMutation } from '@api/clientUserManagement';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import { useDispatch } from 'react-redux';

const UPDATE_USER_ACCESS_DIALOG_COPY: Record<
  USER_ACCESS_MODAL_MODE.EDIT_ACCESS,
  {
    title: string;
    submitLabel: string;
    submittingLabel: string;
  }
> = {
  [USER_ACCESS_MODAL_MODE.EDIT_ACCESS]: {
    title: 'Edit Access',
    submitLabel: 'Save Changes',
    submittingLabel: 'Saving...',
  },
};

type UpdateUserAccessModalProps = {
  open: boolean;
  selectedUser: ClientUser;
  clientCode: string;
  dashboardsList: UserAccess[];
  mode: USER_ACCESS_MODAL_MODE.EDIT_ACCESS;
  onClose: () => void;
  onSuccess: () => void;
};

const UpdateUserAccessModal: FC<UpdateUserAccessModalProps> = ({
  open,
  mode,
  selectedUser,
  clientCode,
  dashboardsList,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  const { title, submitLabel, submittingLabel } = UPDATE_USER_ACCESS_DIALOG_COPY[mode];

  const initialDashboardIds = useMemo(
    () => extractSubmoduleIds(selectedUser.userAccesses),
    [selectedUser.membershipGrantId],
  );

  const {
    selectedRole,
    setSelectedRole,
    selectedDashboardIds,
    dashboardAccordionExpanded,
    setDashboardAccordionExpanded,
    handleDashboardToggle,
    selectAllDashboards,
    clearAllDashboards,
    dashboardOptions,
  } = useUserAccessFormState({
    open,
    initialRole: selectedUser.companyRole,
    initialDashboardIds,
    dashboardsList,
  });

  const [updateUserAccess, { isLoading: isUpdating }] = useUpdateUserAccessMutation();

  const submitButtonText = isUpdating ? submittingLabel : submitLabel;

  const isSubmitEnabled = useMemo(() => {
    if (isUpdating) return false;
    if (selectedRole === CLIENT_USER_ROLE.MEMBER && selectedDashboardIds.length === 0) return false;
    return true;
  }, [isUpdating, selectedRole, selectedDashboardIds]);

  const handleClose = () => {
    if (isUpdating) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedUser.email || !selectedUser.membershipGrantId) return;
    const isRestrictedMember = selectedRole === CLIENT_USER_ROLE.MEMBER;
    try {
      await updateUserAccess({
        clientCode,
        membershipGrantId: selectedUser.membershipGrantId,
        companyRole: selectedRole,
        ...(isRestrictedMember && { submoduleIds: selectedDashboardIds }),
      }).unwrap();
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.SUCCESS,
          message: USER_ACTION_SUCCESS_TOASTS.CHANGES_SAVED,
        }),
      );
      onSuccess();
      onClose();
    } catch (error) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: 'Error while saving changes',
        }),
      );
    }
  };

  return (
    <UserAccessDialogLayout
      open={open}
      title={title}
      submitLabel={submitButtonText}
      canSubmit={isSubmitEnabled}
      isSubmitting={isUpdating}
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      <UserInfoSection userName={selectedUser.username ?? ''} email={selectedUser.email} />
      <UserAccessForm
        selectedRole={selectedRole}
        dashboardOptions={dashboardOptions}
        selectedDashboardIds={selectedDashboardIds}
        dashboardAccordionExpanded={dashboardAccordionExpanded}
        onSelectRole={setSelectedRole}
        onToggleDashboard={handleDashboardToggle}
        onSelectAllDashboards={selectAllDashboards}
        onClearAllDashboards={clearAllDashboards}
        onAccordionExpand={setDashboardAccordionExpanded}
      />
    </UserAccessDialogLayout>
  );
};

export default UpdateUserAccessModal;
